import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getCurrentUserRequest,
  updateUserNamesRequest,
  updateEmailRequest,
  updateOperativeMailsRequest,
  requestSecureCodeRequest,
  verifySecureCodeRequest,
  changePasswordRequest,
} from './userApi';
import { profileUpdated } from '../auth/authSlice';
import { extractApiError } from '../../utils/errors';

/**
 * The id the write endpoints are keyed by.
 *
 * From the session and not from `get_user/`: see the note in `userApi.js`, the
 * driver profile overwrites the id in that payload with its own.
 */
const sessionUserId = (getState) => getState().auth.user?.id;

export const fetchCurrentUser = createAsyncThunk(
  'user/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getCurrentUserRequest();
      return data;
    } catch (error) {
      console.error('Error fetching current user:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo obtener tu información'));
    }
  }
);

/**
 * Saves name and email, which are two endpoints and therefore two requests.
 *
 * **The email goes first, and a failure there stops the run.** It is the only
 * one of the two that can be rejected for a real reason — the address belongs
 * to somebody else — and doing it last would leave the name already saved
 * against an error message, with no way for the user to tell what went through.
 *
 * Only what actually changed is sent, so re-saving an untouched field costs
 * nothing and cannot fail.
 */
export const updateProfile = createAsyncThunk(
  'user/updateProfile',
  async ({ first_name, last_name, email }, { getState, dispatch, rejectWithValue }) => {
    const userId = sessionUserId(getState);
    const current = getState().user.data;

    const emailChanged = email !== undefined && email !== current?.email;
    const namesChanged =
      (first_name !== undefined && first_name !== current?.first_name) ||
      (last_name !== undefined && last_name !== current?.last_name);

    if (!emailChanged && !namesChanged) return current;

    try {
      let updated = current;

      if (emailChanged) {
        const { data } = await updateEmailRequest(userId, email);
        updated = data;
      }

      if (namesChanged) {
        const { data } = await updateUserNamesRequest(userId, { first_name, last_name });
        updated = data;
      }

      dispatch(
        profileUpdated({
          first_name: updated.first_name,
          last_name: updated.last_name,
          email: updated.email,
        })
      );

      return updated;
    } catch (error) {
      console.error('Error updating profile:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron guardar los cambios'));
    }
  }
);

export const updateOperativeMails = createAsyncThunk(
  'user/updateOperativeMails',
  async (mails, { getState, rejectWithValue }) => {
    try {
      const { data } = await updateOperativeMailsRequest(sessionUserId(getState), mails);
      return data;
    } catch (error) {
      console.error('Error updating operative mails:', error);
      return rejectWithValue(
        extractApiError(error, 'No se pudieron guardar los correos de reportería')
      );
    }
  }
);

export const requestSecureCode = createAsyncThunk(
  'user/requestSecureCode',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await requestSecureCodeRequest();
      return data;
    } catch (error) {
      console.error('Error requesting secure code:', error);
      return rejectWithValue(
        extractApiError(error, 'No se pudo enviar el código a tu correo')
      );
    }
  }
);

/**
 * Trades the six-digit code for the token the password change needs.
 *
 * A 400 here is either a wrong code or an expired one and the backend says
 * which, so its message is passed through untouched rather than replaced with a
 * guess.
 */
export const verifySecureCode = createAsyncThunk(
  'user/verifySecureCode',
  async (code, { rejectWithValue }) => {
    try {
      const { data } = await verifySecureCodeRequest(code);
      if (!data?.valid || !data?.token) {
        return rejectWithValue(data?.error || 'Código incorrecto o expirado');
      }
      return data.token;
    } catch (error) {
      console.error('Error verifying secure code:', error);
      return rejectWithValue(extractApiError(error, 'Código incorrecto o expirado'));
    }
  }
);

/**
 * The last step, after which **the session is gone**.
 *
 * Django rotates the session auth hash inside `set_password`, so the cookie the
 * app is holding stops authenticating on the very next request. Whoever calls
 * this has to close the session deliberately; letting the next screen discover
 * it means a 401 and a silent bounce to the login with no explanation.
 */
export const changePassword = createAsyncThunk(
  'user/changePassword',
  async ({ token, password }, { rejectWithValue }) => {
    try {
      const { data } = await changePasswordRequest(token, password);
      return data;
    } catch (error) {
      console.error('Error changing password:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo cambiar la contraseña'));
    }
  }
);
